import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Chip,
  InputBase,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import PublicAccessPolicyEditor from '../access/PublicAccessPolicyEditor';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import PublicIcon from '@mui/icons-material/Public';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { getDynamicReviewTaskStore } from './useDynamicReviewTaskStore';
import DynamicFormFieldDesigner from '../pages/forms/DynamicFormFieldDesigner';
import { useFormPalette } from '../pages/forms/DynamicFormPrimitives';
import { NODE_UI } from '../NodeUI/nodeUiStyles';
import NodePlaceholderAutocomplete from '../NodeUI/NodePlaceholderAutocomplete';
import PeoplePicker from '../common/PeoplePicker';
import useTaskInboxStore from '../pages/useTaskInboxStore';

/* ── Assignment mode constants───────────────────────────────────────── */

const MODES = {
  ONE_PERSON: 'ONE_PERSON',
  MANY_PEOPLE: 'MANY_PEOPLE',
  ANYONE_ELIGIBLE: 'ANYONE_ELIGIBLE',
};

const MODE_CONFIG = [
  { value: MODES.ONE_PERSON, label: 'One person', icon: PersonIcon, hint: 'Assign to a single reviewer' },
  { value: MODES.MANY_PEOPLE, label: 'Multiple people', icon: GroupIcon, hint: 'Assign to several reviewers' },
  { value: MODES.ANYONE_ELIGIBLE, label: 'Anyone eligible', icon: PublicIcon, hint: 'Any eligible user can claim' },
];

// Legacy access mode descriptions kept for reference; UI now uses PublicAccessPolicyEditor

const RESPONSE_MODE_DESCRIPTIONS = {
  FILL_SUBMIT: 'Reviewer fills out the form and submits. No drafts are saved on the server.',
  FILL_REEDIT: 'Reviewer can save drafts and return via a unique URL to continue editing. Drafts are stored on the server and automatically expire after 30 days.',
  VIEW_ONLY: 'Reviewer can view the form but cannot edit or submit responses.',
};

/* ── Helpers ─────────────────────────────────────────────────────────── */

function getResponsibility(taskConfig) {
  return taskConfig?.responsibility || {};
}

function isPlaceholder(value) {
  return typeof value === 'string' && /\$\{.+\}/.test(value);
}

/* ── Section wrapper ─────────────────────────────────────────────────── */

function SettingsSection({ title, children, palette }) {
  return (
    <Box
      sx={{
        border: `1px solid ${palette.border}`,
        borderRadius: NODE_UI.borderRadius,
        backgroundColor: palette.surface,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: '12px',
          py: '7px',
          backgroundColor: palette.surfaceSubtle,
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: NODE_UI.fontSize.value,
            fontWeight: 600,
            color: palette.textPrimary,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: '12px', py: '10px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {children}
      </Box>
    </Box>
  );
}

/* ── Field label ─────────────────────────────────────────────────────── */

function FieldLabel({ children, hint, palette }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '4px' }}>
      <Typography
        sx={{
          fontSize: NODE_UI.fontSize.label,
          fontWeight: NODE_UI.fontWeight.label,
          color: palette.textSecondary,
          lineHeight: 1.4,
          userSelect: 'none',
        }}
      >
        {children}
      </Typography>
      {hint && (
        <Tooltip title={hint} arrow placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 13, color: palette.textSecondary, opacity: 0.5, cursor: 'help' }} />
        </Tooltip>
      )}
    </Box>
  );
}

function SectionSelect({ label, value, onChange, options, theme, palette }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <FieldLabel palette={palette}>{label}</FieldLabel>
      <Select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        sx={{
          fontFamily: 'inherit',
          fontSize: NODE_UI.fontSize.value,
          color: palette.textPrimary,
          backgroundColor: theme.palette.background.default,
          borderRadius: NODE_UI.borderRadiusSm,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.textSecondary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.textPrimary,
          },
          '& .MuiSelect-select': {
            padding: NODE_UI.input.padding,
          },
        }}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </Select>
    </Box>
  );
}

/* ── Mode toggle ─────────────────────────────────────────────────────── */

function AssignmentModeToggle({ activeMode, onChange, palette, theme }) {
  return (
    <Box>
      <FieldLabel palette={palette}>Assignment mode</FieldLabel>
      <ToggleButtonGroup
        value={activeMode}
        exclusive
        onChange={(_, val) => { if (val) onChange(val); }}
        size="small"
        fullWidth
        sx={{
          '& .MuiToggleButtonGroup-grouped': {
            border: `1px solid ${palette.border}`,
            borderRadius: `${NODE_UI.borderRadius} !important`,
            mx: '2px',
            '&:first-of-type': { ml: 0 },
            '&:last-of-type': { mr: 0 },
            textTransform: 'none',
            fontSize: NODE_UI.fontSize.label,
            fontWeight: 500,
            color: palette.textSecondary,
            py: '5px',
            px: '8px',
            gap: '5px',
            transition: 'all 0.15s ease',
            '&.Mui-selected': {
              backgroundColor: palette.surfaceSubtle,
              color: palette.textPrimary,
              fontWeight: 600,
              borderColor: palette.textSecondary,
              '&:hover': {
                backgroundColor: palette.surfaceSubtle,
              },
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          },
        }}
      >
        {MODE_CONFIG.map(({ value, label, icon: Icon }) => (
          <ToggleButton key={value} value={value} disableRipple>
            <Icon sx={{ fontSize: 15 }} />
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Typography
        sx={{
          fontSize: NODE_UI.fontSize.caption,
          color: palette.textSecondary,
          opacity: 0.6,
          mt: '4px',
        }}
      >
        {MODE_CONFIG.find((m) => m.value === activeMode)?.hint}
      </Typography>
    </Box>
  );
}

/* ── Placeholder toggle chip ─────────────────────────────────────────── */

function PlaceholderToggle({ usePlaceholder, onToggle, palette }) {
  return (
    <Chip
      label={usePlaceholder ? 'Using placeholder' : 'Use placeholder'}
      size="small"
      variant={usePlaceholder ? 'filled' : 'outlined'}
      onClick={onToggle}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 500,
        fontFamily: 'monospace',
        borderColor: palette.border,
        color: usePlaceholder ? palette.textPrimary : palette.textSecondary,
        backgroundColor: usePlaceholder ? palette.surfaceSubtle : 'transparent',
        cursor: 'pointer',
        '&:hover': { borderColor: palette.textSecondary },
      }}
    />
  );
}

/* ── Cover image setting ─────────────────────────────────────────────── */

function CoverImageSetting({ value, onChange, palette }) {
  const [previewError, setPreviewError] = useState(false);

  const handleChange = useCallback((e) => {
    setPreviewError(false);
    onChange(e.target.value);
  }, [onChange]);

  const handleClear = useCallback(() => {
    setPreviewError(false);
    onChange('');
  }, [onChange]);

  const showPreview = value && !previewError;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <FieldLabel palette={palette}>Cover image URL</FieldLabel>
      <Box sx={{ position: 'relative' }}>
        <InputBase
          value={value || ''}
          onChange={handleChange}
          placeholder="https://example.com/image.jpg"
          fullWidth
          sx={{
            fontFamily: 'inherit',
            fontSize: NODE_UI.fontSize.value,
            color: palette.textPrimary,
            border: `1px solid ${palette.border}`,
            borderRadius: NODE_UI.borderRadiusSm,
            px: '8px',
            py: '5px',
            transition: 'border-color 0.15s ease',
            '&:hover': { borderColor: palette.textSecondary },
            '&.Mui-focused': { borderColor: palette.textPrimary },
            '& input::placeholder': { color: palette.textSecondary, opacity: 0.5 },
          }}
        />
        {value && (
          <Box
            onClick={handleClear}
            sx={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: palette.chipBg,
              '&:hover': { backgroundColor: palette.border },
            }}
          >
            <CloseIcon sx={{ fontSize: 12, color: palette.textSecondary }} />
          </Box>
        )}
      </Box>
      {showPreview ? (
        <Box
          sx={{
            width: '100%',
            height: 80,
            borderRadius: '6px',
            overflow: 'hidden',
            border: `1px solid ${palette.border}`,
          }}
        >
          <Box
            component="img"
            src={value}
            alt="Cover preview"
            onError={() => setPreviewError(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: 48,
            borderRadius: '6px',
            border: `1px dashed ${palette.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <ImageOutlinedIcon sx={{ fontSize: 16, color: palette.textSecondary, opacity: 0.4 }} />
          <Typography sx={{ fontSize: 11, color: palette.textSecondary, opacity: 0.4 }}>
            {previewError ? 'Could not load image' : 'No cover image set'}
          </Typography>
        </Box>
      )}
      <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: palette.textSecondary, opacity: 0.5 }}>
        Displayed at the top of the form page. Leave empty for default.
      </Typography>
    </Box>
  );
}

/* ── One Person panel ────────────────────────────────────────────────── */

function OnePersonPanel({ responsibility, onUpdate, users, usersLoading, palette, availablePlaceholders }) {
  const onePerson = responsibility.onePerson || {};
  const currentId = onePerson.assigneeUserId || '';
  const currentIds = Array.isArray(onePerson.assigneeUserIds) ? onePerson.assigneeUserIds : [];
  const [usePlaceholder, setUsePlaceholder] = useState(() => isPlaceholder(currentId));

  const handleUserToggle = useCallback((userId) => {
    const updated = {
      ...onePerson,
      assigneeUserId: userId,
      assigneeUserIds: [userId],
    };
    onUpdate({ ...responsibility, onePerson: updated });
  }, [onePerson, responsibility, onUpdate]);

  const handlePlaceholderChange = useCallback((val) => {
    const updated = {
      ...onePerson,
      assigneeUserId: val,
      assigneeUserIds: val.trim() ? [val.trim()] : [],
    };
    onUpdate({ ...responsibility, onePerson: updated });
  }, [onePerson, responsibility, onUpdate]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <FieldLabel palette={palette} hint="Select the user who will review this task">
          Assignee
        </FieldLabel>
        <PlaceholderToggle
          usePlaceholder={usePlaceholder}
          onToggle={() => setUsePlaceholder((p) => !p)}
          palette={palette}
        />
      </Box>

      {usePlaceholder ? (
        <NodePlaceholderAutocomplete
          value={currentId}
          onChange={handlePlaceholderChange}
          availablePlaceholders={availablePlaceholders}
          placeholder="Type $ to insert a placeholder"
        />
      ) : (
        <PeoplePicker
          items={users}
          selectedIds={currentIds.filter(Boolean)}
          onToggle={handleUserToggle}
          getId={(u) => u.userName}
          getName={(u) => u.displayName || u.userName}
          getDetail={(u) => u.email || u.userName}
          getAvatarLabel={(u) => u.displayName || u.userName || 'User'}
          loading={usersLoading}
          placeholder="Search for a user..."
          emptyStateLabel="No users available"
        />
      )}
    </Box>
  );
}

/* ── Many People panel ───────────────────────────────────────────────── */

function ManyPeoplePanel({ responsibility, onUpdate, users, usersLoading, palette }) {
  const manyPeople = responsibility.manyPeople || {};
  const currentIds = Array.isArray(manyPeople.assigneeUserIds) ? manyPeople.assigneeUserIds : [];

  const handleToggle = useCallback((userId) => {
    const next = currentIds.includes(userId)
      ? currentIds.filter((id) => id !== userId)
      : [...currentIds, userId];
    onUpdate({
      ...responsibility,
      manyPeople: { ...manyPeople, assigneeUserIds: next },
    });
  }, [currentIds, manyPeople, responsibility, onUpdate]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <FieldLabel palette={palette} hint="All selected users will receive this review task">
        Assignees
      </FieldLabel>
      <PeoplePicker
        items={users}
        selectedIds={currentIds}
        onToggle={handleToggle}
        getId={(u) => u.userName}
        getName={(u) => u.displayName || u.userName}
        getDetail={(u) => u.email || u.userName}
        getAvatarLabel={(u) => u.displayName || u.userName || 'User'}
        loading={usersLoading}
        placeholder="Search for users..."
        emptyStateLabel="No users available"
      />
    </Box>
  );
}

/* ── Anyone Eligible panel ───────────────────────────────────────────── */

function AnyoneEligiblePanel({ responsibility, onUpdate, users, usersLoading, palette }) {
  const anyoneEligible = responsibility.anyoneEligible || {};
  const eligibleIds = Array.isArray(anyoneEligible.eligibleUserIds) ? anyoneEligible.eligibleUserIds : [];
  const allowAnonymous = Boolean(anyoneEligible.allowAnonymousWhenPublicLink);

  const handleToggle = useCallback((userId) => {
    const next = eligibleIds.includes(userId)
      ? eligibleIds.filter((id) => id !== userId)
      : [...eligibleIds, userId];
    onUpdate({
      ...responsibility,
      anyoneEligible: { ...anyoneEligible, eligibleUserIds: next },
    });
  }, [eligibleIds, anyoneEligible, responsibility, onUpdate]);

  const handleAnonymousToggle = useCallback((e) => {
    onUpdate({
      ...responsibility,
      anyoneEligible: {
        ...anyoneEligible,
        allowAnonymousWhenPublicLink: e.target.checked,
        ...(e.target.checked ? { eligibleUserIds: [] } : {}),
      },
    });
  }, [anyoneEligible, responsibility, onUpdate]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={allowAnonymous}
            onChange={handleAnonymousToggle}
            size="small"
            sx={{
              color: palette.border,
              '&.Mui-checked': { color: palette.textPrimary },
              p: '4px',
            }}
          />
        }
        label={
          <Typography sx={{ fontSize: NODE_UI.fontSize.label, color: palette.textSecondary }}>
            Allow anonymous submissions via public link
          </Typography>
        }
        sx={{ ml: -0.5, gap: '2px' }}
      />
      {!allowAnonymous && (
        <Box>
          <FieldLabel palette={palette} hint="Users who can claim this task (leave empty for all team members)">
            Eligible users
          </FieldLabel>
          <PeoplePicker
            items={users}
            selectedIds={eligibleIds}
            onToggle={handleToggle}
            getId={(u) => u.userName}
            getName={(u) => u.displayName || u.userName}
            getDetail={(u) => u.email || u.userName}
            getAvatarLabel={(u) => u.displayName || u.userName || 'User'}
            loading={usersLoading}
            placeholder="Search eligible users..."
            emptyStateLabel="No users available"
          />
        </Box>
      )}
    </Box>
  );
}

/* ── Tab bar ─────────────────────────────────────────────────────────── */

function TabBar({ tabs, active, onChange, palette }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '2px',
        p: '3px',
        backgroundColor: palette.surfaceSubtle,
        borderRadius: '8px',
        border: `1px solid ${palette.border}`,
        mb: '12px',
      }}
    >
      {tabs.map((tab, i) => (
        <Box
          key={tab}
          onClick={() => onChange(i)}
          sx={{
            flex: 1,
            textAlign: 'center',
            py: '5px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: NODE_UI.fontSize.value,
            fontWeight: active === i ? 600 : 400,
            color: active === i ? palette.textPrimary : palette.textSecondary,
            backgroundColor: active === i ? palette.surface : 'transparent',
            boxShadow: active === i ? NODE_UI.shadow : 'none',
            transition: 'all 0.15s ease',
            userSelect: 'none',
          }}
        >
          {tab}
        </Box>
      ))}
    </Box>
  );
}

/* ── Main component ──────────────────────────────────────────────────── */

export default function DynamicReviewTaskTemplate({
  component_id,
  projectId,
  setChildTemplateLoaded,
  availablePlaceholders,
}) {
  const store = useMemo(() => getDynamicReviewTaskStore(component_id), [component_id]);
  const data = store((s) => s.data);
  const loaded = store((s) => s.loaded);
  const palette = useFormPalette();
  const theme = useTheme();

  const [tab, setTab] = useState(0);

  /* Fetch team members */
  const activeAccountUsers = useTaskInboxStore((s) => s.activeAccountUsers);
  const activeAccountUsersLoading = useTaskInboxStore((s) => s.activeAccountUsersLoading);
  const fetchActiveAccountUsers = useTaskInboxStore((s) => s.fetchActiveAccountUsers);

  useEffect(() => {
    fetchActiveAccountUsers();
  }, [fetchActiveAccountUsers]);

  useEffect(() => {
    if (loaded && setChildTemplateLoaded) setChildTemplateLoaded(true);
  }, [loaded, setChildTemplateLoaded]);

  /* Store actions */
  const handleFieldsChange = useCallback(
    (fields) => store.getState().setFields(fields),
    [store]
  );
  const handleTitleChange = useCallback(
    (val) => store.getState().setTitle(val),
    [store]
  );
  const handleObjectiveChange = useCallback(
    (val) => store.getState().setObjective(val),
    [store]
  );
  const handleDescriptionChange = useCallback(
    (val) => store.getState().setDescription(val),
    [store]
  );
  const handleAccessPolicyChange = useCallback(
    (val) => store.getState().setAccessPolicy(val),
    [store]
  );
  const handleResponseModeChange = useCallback(
    (val) => store.getState().setResponseMode(val),
    [store]
  );

  /* ── Responsibility helpers ──────────────────────────────────────── */

  const responsibility = useMemo(() => getResponsibility(data.taskConfig), [data.taskConfig]);
  const activeMode = responsibility.activeMode || MODES.ONE_PERSON;

  const handleModeChange = useCallback(
    (mode) => {
      store.getState().setTaskConfig({
        responsibility: { ...responsibility, activeMode: mode },
      });
    },
    [store, responsibility]
  );

  const handleResponsibilityUpdate = useCallback(
    (updated) => {
      store.getState().setTaskConfig({
        responsibility: { ...updated, activeMode: activeMode },
      });
    },
    [store, activeMode]
  );

  /* ── Email notify ────────────────────────────────────────────────── */

  const emailConfig = data.taskConfig?.notifications?.email || {};
  const emailNotifyEnabled = Boolean(emailConfig.enabled);
  const emailNotifyUserIds = Array.isArray(emailConfig.userIds) ? emailConfig.userIds : [];

  const handleCoverImageChange = useCallback(
    (val) => store.getState().setFormSettings({ coverImageUrl: val }),
    [store]
  );

  const handleEmailToggle = useCallback(
    (e) => {
      const enabled = e.target.checked;
      store.getState().setTaskConfig({
        notifications: {
          ...data.taskConfig?.notifications,
          email: {
            ...(data.taskConfig?.notifications?.email || {}),
            enabled,
            ...(!enabled ? { userIds: [] } : {}),
          },
        },
      });
    },
    [store, data.taskConfig]
  );

  const handleEmailUserToggle = useCallback(
    (userId) => {
      const next = emailNotifyUserIds.includes(userId)
        ? emailNotifyUserIds.filter((id) => id !== userId)
        : [...emailNotifyUserIds, userId];
      store.getState().setTaskConfig({
        notifications: {
          ...data.taskConfig?.notifications,
          email: {
            ...(data.taskConfig?.notifications?.email || {}),
            enabled: next.length > 0,
            userIds: next,
          },
        },
      });
    },
    [store, emailNotifyUserIds, data.taskConfig]
  );

  if (!loaded) return null;

  const objectiveField = (
    <Box sx={{ mt: 1 }}>
      <InputBase
        value={data.objective || ''}
        onChange={(e) => handleObjectiveChange(e.target.value)}
        placeholder="What the reviewer needs to do (objective)..."
        multiline
        minRows={1}
        fullWidth
        sx={{
          fontFamily: 'inherit',
          fontSize: '12px',
          color: palette.textPrimary,
          border: `1px dashed ${palette.border}`,
          borderRadius: '4px',
          padding: '6px 8px',
          lineHeight: 1.5,
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          '&:hover': { borderColor: palette.textSecondary },
          '&.Mui-focused': { borderColor: palette.textPrimary, borderStyle: 'solid' },
          '& textarea::placeholder': { color: palette.textSecondary, opacity: 0.4 },
        }}
      />
    </Box>
  );

  /* ── Assignment mode panel ───────────────────────────────────────── */

  const assignmentPanel =
    activeMode === MODES.ONE_PERSON ? (
      <OnePersonPanel
        responsibility={responsibility}
        onUpdate={handleResponsibilityUpdate}
        users={activeAccountUsers}
        usersLoading={activeAccountUsersLoading}
        palette={palette}
        availablePlaceholders={availablePlaceholders}
      />
    ) : activeMode === MODES.MANY_PEOPLE ? (
      <ManyPeoplePanel
        responsibility={responsibility}
        onUpdate={handleResponsibilityUpdate}
        users={activeAccountUsers}
        usersLoading={activeAccountUsersLoading}
        palette={palette}
      />
    ) : (
      <AnyoneEligiblePanel
        responsibility={responsibility}
        onUpdate={handleResponsibilityUpdate}
        users={activeAccountUsers}
        usersLoading={activeAccountUsersLoading}
        palette={palette}
      />
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <TabBar
        tabs={['Review Form', 'Task Settings']}
        active={tab}
        onChange={setTab}
        palette={palette}
      />

      {tab === 0 && (
        <DynamicFormFieldDesigner
          fields={data.fields}
          onChange={handleFieldsChange}
          title={data.title}
          description={data.description}
          onTitleChange={handleTitleChange}
          onDescriptionChange={handleDescriptionChange}
          headerExtra={objectiveField}
          availablePlaceholders={availablePlaceholders}
        />
      )}

      {tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <SettingsSection title="General" palette={palette}>
            <PublicAccessPolicyEditor
              accessPolicy={data.accessPolicy}
              onChange={handleAccessPolicyChange}
              users={activeAccountUsers}
              usersLoading={activeAccountUsersLoading}
            />
            <SectionSelect
              label="Response Mode"
              value={data.responseMode}
              onChange={handleResponseModeChange}
              options={[
                { value: 'FILL_SUBMIT', label: 'Submit only' },
                { value: 'FILL_REEDIT', label: 'Save & resume drafts' },
                { value: 'VIEW_ONLY', label: 'View only' },
              ]}
              theme={theme}
              palette={palette}
            />
            <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: palette.textSecondary, opacity: 0.75 }}>
              {RESPONSE_MODE_DESCRIPTIONS[data.responseMode] || ''}
            </Typography>
          </SettingsSection>

          {/* ── Assignment section ──────────────────────────────────── */}
          <SettingsSection title="Assignment" palette={palette}>
            <AssignmentModeToggle
              activeMode={activeMode}
              onChange={handleModeChange}
              palette={palette}
              theme={theme}
            />
            {assignmentPanel}
          </SettingsSection>

          {/* ── Notifications section ──────────────────────────────── */}
          <SettingsSection title="Notifications" palette={palette}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={emailNotifyEnabled}
                  onChange={handleEmailToggle}
                  size="small"
                  sx={{
                    color: palette.border,
                    '&.Mui-checked': { color: palette.textPrimary },
                    p: '4px',
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: NODE_UI.fontSize.value, fontWeight: 500, color: palette.textPrimary }}>
                  Send email notifications
                </Typography>
              }
              sx={{ ml: -0.5, gap: '2px' }}
            />

            {emailNotifyEnabled && (
              <Box>
                <FieldLabel palette={palette} hint="Selected users will be emailed when this task is created">
                  Notify users
                </FieldLabel>
                <PeoplePicker
                  items={activeAccountUsers}
                  selectedIds={emailNotifyUserIds}
                  onToggle={handleEmailUserToggle}
                  getId={(u) => u.userName}
                  getName={(u) => u.displayName || u.userName}
                  getDetail={(u) => u.email || u.userName}
                  getAvatarLabel={(u) => u.displayName || u.userName || 'User'}
                  loading={activeAccountUsersLoading}
                  placeholder="Search for users..."
                  emptyStateLabel="No users available"
                />
              </Box>
            )}
          </SettingsSection>

          {/* ── Appearance section ─────────────────────────────────── */}
          <SettingsSection title="Appearance" palette={palette}>
            <CoverImageSetting
              value={data.formSettings?.coverImageUrl || ''}
              onChange={handleCoverImageChange}
              palette={palette}
            />
          </SettingsSection>
        </Box>
      )}
    </Box>
  );
}
